import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery } from '@tanstack/vue-query';
import { MaybeRef } from '@vueuse/core';
import { minutesToMilliseconds } from 'date-fns';
import type { MaybeRefOrGetter } from '@vueuse/core';

import { getDateDiffFromNow, isFetchError } from '~~core';

import { fetchDocument, fetchDocumentList, fetchDocumentLegacyPreview, fetchRubricates } from '../services/document';

export type UseFetchLegacyPreviewOptions = {
  enabled?: MaybeRef<boolean>;
};

export type UseFetchRubricatesOptions = 'initials' | 'manuscript' | 'both' | undefined;

export const documentQueryKeys = createQueryKeys('document', {
  show: (requirementKey: MaybeRefOrGetter<string>, documentKey: MaybeRefOrGetter<string>) => [{ requirementKey, documentKey }],
  index: (requirementKey: MaybeRefOrGetter<string>) => [{ requirementKey }],
  legacyPreview: (signatureRequestKey: MaybeRefOrGetter<string>, documentKey: MaybeRefOrGetter<string>) => [{ documentKey, signatureRequestKey }],
  rubricates: (signatureRequestKey: MaybeRefOrGetter<string>, type: MaybeRefOrGetter<UseFetchRubricatesOptions>) => ['rubricate', { signatureRequestKey }, { type }],
});

export function useFetchDocument(requirementKey: MaybeRefOrGetter<string>, documentKey: MaybeRefOrGetter<string>) {
  const result = useQuery({
    queryKey: documentQueryKeys.show(requirementKey, documentKey).queryKey,
    queryFn: () => fetchDocument(toValue(requirementKey), toValue(documentKey)),
    staleTime: minutesToMilliseconds(10),
    refetchOnWindowFocus: false,
    retry: (attemps, error) => (isFetchError(error) && error.statusCode === 404 ? false : attemps < 3),
  });

  return {
    ...result,
    document: result.data,
  };
}

export function useFetchLegacyPreview(signatureRequestKey: MaybeRefOrGetter<string>, documentKey: MaybeRefOrGetter<string>, options: UseFetchLegacyPreviewOptions = {}) {
  const result = useQuery({
    queryKey: documentQueryKeys.legacyPreview(signatureRequestKey, documentKey).queryKey,
    queryFn: () => fetchDocumentLegacyPreview(toValue(signatureRequestKey), toValue(documentKey)),
    staleTime: minutesToMilliseconds(30),
    refetchOnWindowFocus: false,
    enabled: options.enabled,
  });

  return {
    ...result,
    legacyPreview: result.data,
  };
}

export function useFetchDocumentsList(signatureRequestKey: MaybeRefOrGetter<string>) {
  const result = useQuery({
    queryKey: documentQueryKeys.index(signatureRequestKey).queryKey,
    queryFn: () => fetchDocumentList(toValue(signatureRequestKey)),
    staleTime: minutesToMilliseconds(1),
    initialDataUpdatedAt: 0,
    initialData: [],
  });

  return {
    ...result,
    documents: result.data,
  };
}

export function useFetchRubricates(signatureRequestKey: MaybeRefOrGetter<string>, type: MaybeRefOrGetter<UseFetchRubricatesOptions>) {
  const result = useQuery({
    queryKey: documentQueryKeys.rubricates(signatureRequestKey, type).queryKey,
    queryFn: () => fetchRubricates(toValue(signatureRequestKey), toValue(type)),
    staleTime: 50,
    initialDataUpdatedAt: 0,
    refetchInterval(data, query) {
      if (isFetchError(query.state.error) && query.state.error.status === 404) return false;

      if (!data) return 500;

      return getDateDiffFromNow(data.expireAt);
    },
    refetchOnWindowFocus: false,
    retry: (attemps, error) => (isFetchError(error) && error.statusCode === 404 ? false : attemps < 3),
  });

  return {
    ...result,
    rubricates: result.data,
  };
}
